import CloseIcon from "@/core/ui/iconsax/linear/custom-x.svg"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import { DiscoIconButton, DiscoText, DiscoTooltip } from "@disco-ui"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import { ClassNameMap } from "@material-ui/core/styles/withStyles"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"

interface EntityTagProps extends TestIDProps {
  name: string
  onClick?: () => void
  onRemove?: () => void
  isSelected?: boolean
  classes?: ClassNameMap<"tag">
  errors?: string[]
}

function EntityTag({
  testid = "EntityTag",
  name,
  onClick,
  onRemove,
  isSelected = false,
  classes: customClasses,
  errors,
}: EntityTagProps) {
  const classes = useStyles({
    hoverable: Boolean(onClick),
    rightIcon: Boolean(onRemove),
    hasError: Boolean(errors?.length),
    isSelected,
  })

  const content = (
    <DiscoTooltip content={errors?.[0]} disabled={!errors?.length}>
      <div
        data-testid={`${testid}.tag.${name}`}
        className={classNames(classes.tag, customClasses?.tag)}
      >
        <DiscoText variant={"body-sm-500"} truncateText={1}>
          {name}
        </DiscoText>

        {onRemove && (
          <DiscoIconButton
            testid={`${testid}.tag.${name}.remove-button`}
            onClick={onRemove}
            className={classes.removeButton}
          >
            <CloseIcon />
          </DiscoIconButton>
        )}
      </div>
    </DiscoTooltip>
  )

  if (!onClick) return content

  return <DiscoContainerButton onClick={onClick}>{content}</DiscoContainerButton>
}

type StypeProps = {
  hoverable?: boolean
  rightIcon?: boolean
  isSelected?: boolean
  hasError?: boolean
}

const useStyles = makeUseStyles((theme) => ({
  tag: ({ hoverable, rightIcon, isSelected, hasError }: StypeProps) => ({
    height: "32px",
    minWidth: "26px",
    maxWidth: "300px",
    width: "max-content",
    border: theme.palette.constants.borderSmall,
    borderRadius: theme.measure.borderRadius.xl,
    borderColor: theme.palette.groovy.neutral[300],
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    ...styleIf(isSelected, {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
    }),

    ...styleIf(hoverable, {
      "&:hover": {
        cursor: "pointer",
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
      },
    }),

    ...styleIf(rightIcon, {
      padding: theme.spacing(1, 0.75, 1, 1.5),
    }),

    ...styleIf(hasError, {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.light,
    }),
  }),
  removeButton: {
    padding: theme.spacing(0.5),

    "& svg": {
      height: 12,
      width: 12,
      "& path": {
        color: theme.palette.text.primary,
      },
    },
  },
}))

export default EntityTag
